import DataMgr from "./DataMgr";
import EventManager from "./EventManager";
import EventTrack from "../Core/EventTrack";
import Util from "@/Master/Util";
import { confJson } from "@/app/config/confJson";
import { fwkConst } from "@/Fwk/FwkConst";
// import Adjust from "@adjustcom/adjust-web-sdk";


/**
 * Adjust 状态枚举
 */
export const adjustState = {
    initSdk: "initSdk",
    attributionSuccess: "attribution",
} as const;

export type Attribution = {
    /** 调整设备标识符 */
    adid: string,
    /** 跟踪器令牌 */
    tracker_token: string,
    /** 追踪器名称 */
    tracker_name: string,
    /** 网络分组级别 */
    network?: string,
    /** 广告系列分组级别 */
    campaign?: string,
    /** 广告组分组级别 */
    adgroup?: string,
    /** 创意分组级别 */
    creative?: string,
    /** 点击标签 */
    click_label?: string,
    /** 归因状态，例如“installed”或“reattributed” */
    state: string
}

/**
 * Adjust 管理器类，用于初始化和管理 Adjust SDK
 */
export default class AdjustMgr {
    static tag: string = `[AdjustMgr]`;
    // static tracker_name: string = `other`;
    // static campaign_name: string = `other`;
    // static adgroup_name: string = `other`;
    // static creative_name: string = `other`;

    static Adjust:any;
    /**
     * 初始化 Adjust SDK
     */
    static Init(): void {
        if (!Util.isWindow) return;

        const _this = this;
        this.Adjust = require('@adjustcom/adjust-web-sdk');

        if (Util.isLog) {
            console.log(
                _this.tag,
                `Init Adjust SDK  adjust_app_token=${confJson.adjust_app_token},adjust_environment=${confJson.adjust_environment},adjust_logLevel=${confJson.adjust_logLevel}`
            );
        }


        // 初始化 Adjust SDK
        this.Adjust.initSdk({
            appToken: confJson.adjust_app_token,        // 替换为你的应用令牌
            environment: confJson.adjust_environment,  // 或者 'production'
            logLevel: confJson.adjust_logLevel,
            attributionCallback: (e: string, attribution: any) => {
                if (Util.isLog) {
                    console.log(_this.tag, 'Adjust attributionCallback: ', attribution);
                    console.log(_this.tag, "Adjust attributionCallback", attribution);
                }
                DataMgr.setStorage(fwkConst.ADJUST_ATTR, attribution);
                EventManager.dispatchEvent(adjustState.attributionSuccess);
                EventTrack.TrackAdjustOk();
            }
        });

        EventManager.dispatchEvent(adjustState.initSdk);
    }

    /**
     * 获取 Web UUID
     */
    static get getWebUUID(): string {
        const storedUUID = DataMgr.getStorage(fwkConst.WEB_UUID);
        // 如果本地存储中存在有效的 UUID，直接返回
        if (typeof storedUUID === 'string' && storedUUID.length > 5) {
            return storedUUID;
        }

        if (typeof this.Adjust !== 'object') {
            return '';
        }

        // 生成一个新的 UUID
        const webUUID: string | undefined = this.Adjust.getWebUUID();
        if (webUUID === undefined) {
            return '';
        }

        if (Util.isLog) {
            console.log(this.tag, `web_uuid={${webUUID}}`);
        }

        // 将新的 UUID 储存在本地
        DataMgr.setStorage(fwkConst.WEB_UUID, webUUID);

        // 返回生成的 UUID
        return webUUID;
    }

    /**
     * 获取 Adjust 渠道归因信息
     */
    static get getAttribution(): any {
        return this.Adjust.getAttribution();
    }

    /**
     * 获取 Adjust 渠道信息归因信息，从 localStorage
     */
    static get getAdjustAttrObj(): string {
        const adjustAttr = DataMgr.getStorage(fwkConst.ADJUST_ATTR);
        if (Util.isLog) {
            console.log(this.tag, JSON.stringify(adjustAttr));
        }
        if (adjustAttr) {
            try {
                return JSON.stringify(adjustAttr);
            } catch (e) {
                console.error(`${this.tag} 解析 Adjust 归因信息时出错:`, e);
            }
        }
        return '{}';
    }

    /**
     * 从归因信息中获取指定字段
     * @param fieldName 需要获取的字段名
     * @param defaultValue 默认值
     * @returns 指定字段的值
     */
    static getAttributeField(fieldName: string, defaultValue: string = 'other'): string {
        if ((this as any)[fieldName] && (this as any)[fieldName] !== 'other') {
            return (this as any)[fieldName];
        }

        const justAttr = this.getAdjustAttrObj || this.getAttribution;

        if (justAttr && typeof justAttr === 'object') {
            (this as any)[fieldName] = justAttr[fieldName] || 'null';
            return (this as any)[fieldName];
        }

        return defaultValue;
    }

    /**
     * 获取渠道 Tracker
     */
    static get getTracker(): string {
        return this.getAttributeField('tracker_name');
    }

    /**
     * 获取 Campaign
     */
    static get getCampaign(): string {
        return this.getAttributeField('campaign_name');
    }

    /**
     * 获取 Adgroup
     */
    static get getAdgroup(): string {
        return this.getAttributeField('adgroup_name');
    }

    /**
     * 获取 Creative
     */
    static get getCreative(): string {
        return this.getAttributeField('creative_name');
    }

    /**
     * 跟踪事件
     * @param obj - 事件对象
     */
    static trackEvent(obj: any): void {
        this.Adjust.trackEvent(obj);
    }

    /**
     * 构建 Facebook URL
     * @param p0 - Tracker Token
     * @param p1 - Campaign 名称
     * @param p2 - Campaign 详细信息
     * @param p3 - Adgroup 名称
     * @param p4 - Adgroup 详细信息
     * @param p5 - Creative 名称
     * @param p6 - Creative 详细信息
     * @param fbclid - Facebook Click ID
     * @param fbpid - Facebook Partner ID
     * @param url - 重定向 URL
     * @returns 构建好的 URL 或 null
     */
    static buildFacebookURL(
        p0: string,
        p1: string,
        p2: string,
        p3: string,
        p4: string,
        p5: string,
        p6: string,
        fbclid: string = "",
        fbpid: string = "",
        url: string = ""
    ): string | null {
        if (!p0) {
            return null;
        }
        const tracker_token = p0;
        const campaign = p1 && p2 ? `${p1}(${p2})` : "";
        const adgroup = p3 && p4 ? `${p3}(${p4})` : "";
        const creative = p5 && p6 ? `${p5}(${p6})` : "";
        let params: Record<string, string> = {
            campaign,
            adgroup,
            creative,
            fbclid,
            fbpid,
        };
        if (url) {
            params['redirect'] = url;
        }
        const newURL = `https://app.adjust.com/${tracker_token}?${Object.entries(params)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join("&")}`;

        return newURL;
    }
}

// // 使用示例
// const adjustManager = AdjustMgr;
// adjustManager.Init();
// const uuid = adjustManager.getWebUUID;
// console.log(`Web UUID: ${uuid}`);
// console.log(`Adjust Attribution: ${adjustManager.getAttribution}`);
// console.log(`Adjust Attribution Obj: ${adjustManager.getAdjustAttrObj}`);
// adjustManager.trackEvent({ /* 事件对象 */ });
// const fbURL = adjustManager.buildFacebookURL('tracker_token', 'campaign', 'details', 'adgroup', 'details', 'creative', 'details');
// console.log(`Facebook URL: ${fbURL}`);
